import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import "@/App.css";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import AppLayout from "@/components/AppLayout";
import AuthCallback from "@/pages/AuthCallback";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import { PrivacyPolicy, TermsOfService, DeleteAccountInfo, Founder } from "@/pages/LegalPages";
import Contact from "@/pages/Contact";
import Dashboard from "@/pages/Dashboard";
import Reminders from "@/pages/Reminders";
import AddReminder from "@/pages/AddReminder";
import ReminderDetail from "@/pages/ReminderDetail";
import CalendarPage from "@/pages/CalendarPage";
import Documents from "@/pages/Documents";
import PeopleAssets from "@/pages/PeopleAssets";
import Renewals from "@/pages/Renewals";
import ImportExport from "@/pages/ImportExport";
import Analytics from "@/pages/Analytics";
import Notifications from "@/pages/Notifications";
import SettingsPage from "@/pages/Settings";
import Admin from "@/pages/Admin";
import NotFound from "@/pages/NotFound";

function Protected({ children, admin }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return <div className="min-h-screen grid place-items-center" data-testid="auth-loading"><div className="w-10 h-10 rounded-full border-4 border-[#E8E5F1] border-t-[var(--pm-primary)] animate-spin" /></div>;
  }
  if (!user) return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  if (admin && !user.is_admin) return <AppLayout><NotFound inApp code={403} /></AppLayout>;
  return <AppLayout>{children}</AppLayout>;
}

const APP_ROUTES = [
  ["/dashboard", <Dashboard />], ["/reminders", <Reminders />], ["/reminders/new", <AddReminder />],
  ["/reminders/:id", <ReminderDetail />], ["/reminders/:id/edit", <AddReminder edit />], ["/calendar", <CalendarPage />],
  ["/documents", <Documents />], ["/people-assets", <PeopleAssets />], ["/renewals", <Renewals />],
  ["/import-export", <ImportExport />], ["/analytics", <Analytics />], ["/notifications", <Notifications />],
  ["/settings", <SettingsPage />],
];

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/terms-of-service" element={<TermsOfService />} />
      <Route path="/delete-account" element={<DeleteAccountInfo />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/founder" element={<Founder />} />
      {APP_ROUTES.map(([p, el]) => <Route key={p} path={p} element={<Protected>{el}</Protected>} />)}
      <Route path="/admin" element={<Protected admin><Admin /></Protected>} />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
        <Toaster position="top-right" richColors closeButton />
      </AuthProvider>
    </BrowserRouter>
  );
}
